import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type {
  InsertControlTestResultMutation,
  InsertControlTestResultMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertControlTestResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type InsertControlTestResultInput = Omit<
  InsertControlTestResultMutationVariables,
  'TestType'
> & {
  TestType?: TestType | null;
};

type InsertControlTestResultTRPCInput = Omit<
  InsertControlTestResultMutationVariables,
  'ControlIds' | 'TestType'
> & {
  ControlIds: string[];
  TestType?: TestType | null;
};

export const useInsertControlTestResult = createMutationHook({
  name: 'insertControlTestResult',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.testResult.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  // Coerce the codegen `string | string[]` ControlIds to a plain array.
  mapInput: ({
    ControlIds,
    ...rest
  }: InsertControlTestResultInput): InsertControlTestResultTRPCInput => ({
    ...rest,
    ControlIds: Array.isArray(ControlIds) ? ControlIds : [ControlIds],
  }),
  invalidate: (trpc) => [
    trpc.frontend.testResult.testResults.queryKey(),
    trpc.frontend.testResult.testResultsByControlId.queryKey(),
    trpc.frontend.testResult.latestTestResultsByControlId.queryKey(),
    trpc.frontend.testResult.testResultById.queryKey(),
    // COI-889: the left-nav badge reads this count family.
    trpc.frontend.testResult.testResultCount.queryKey(),
    // COI-889: the control register's Design / Performance / Overall
    // effectiveness columns read test results; `controlById` does not.
    trpc.frontend.control.pathKey(),
    // COI-471 parity: Apollo twin also evicts `risk_score` (test results
    // roll up into control effectiveness / risk scores),
    // `internal_audit_report` and `oversight_assessment`.
    trpc.frontend.risk.pathKey(),
    trpc.frontend.internalAuditReport.pathKey(),
    trpc.frontend.oversight.pathKey(),
  ],
  mapResponse: (result: {
    Ids: string[];
  }): InsertControlTestResultMutation => ({
    insertControlTestResult: {
      __typename: 'insertControlTestResultOutput',
      Ids: result.Ids,
    },
  }),
  graphqlDocument: InsertControlTestResultDocument,
  graphqlVariables: (variables: InsertControlTestResultInput) => ({
    ...variables,
  }),
  evictFields: [
    'test_result',
    'test_result_aggregate',
    'control',
    'internal_audit_report',
    'oversight_assessment',
    'risk_score',
  ],
});
